import { useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { joinGuestSession } from '@/auth/actions';
import { useGuestSession } from '@/auth/react';
import { Artwork } from '@/components/Artwork';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { Input } from '@/components/Input';
import { isApiError } from '@/api/errors';
import { formatLocation, isLocationType, venueVocabulary } from '@/venue/venue';
import { useTableContext } from './context';
import { Notice, Panel, QuantityStepper, SlowHint } from './parts';
import { LoadingRegion, Skeleton } from '@/components/Skeleton';
import { describeCustomerError } from './wording';

/**
 * `/q/:qrToken` — what a scanned QR code opens. A customer who already has a session for this code
 * goes straight back in; otherwise they give a name (and a guest count at a table) and a session starts.
 * No account, no password.
 */
export function EntryPage() {
  const { qrToken = '' } = useParams();
  const session = useGuestSession();
  const navigate = useNavigate();
  const table = useTableContext(qrToken);

  const [name, setName] = useState('');
  const [guests, setGuests] = useState(1);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [attempted, setAttempted] = useState(false);

  if (session && session.qrToken === qrToken) return <Navigate to="/t/menu" replace />;

  if (table.isPending) return <EntrySkeleton />;

  if (table.isError) {
    if (isApiError(table.error) && table.error.hasCode('INVALID_QR_TOKEN')) {
      const words = describeCustomerError(table.error);
      return (
        <div role="alert">
          <EmptyState icon="alert" title={words.title} message={words.message} />
        </div>
      );
    }
    return <ErrorState error={table.error} onRetry={() => void table.refetch()} />;
  }

  const context = table.data;
  const locationType = isLocationType(context.location_type) ? context.location_type : 'TABLE';
  const vocabulary = venueVocabulary(locationType);
  const failureWords = failure ? describeCustomerError(failure, locationType) : null;

  const nameMissing = name.trim() === '';

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setAttempted(true);
    if (nameMissing) return;
    setBusy(true);
    setFailure(null);
    try {
      await joinGuestSession({
        qrToken,
        displayName: name,
        // A seat holds one guest; only a table asks for a count.
        partySize: vocabulary.showsGuestCount ? guests : 1,
      });
      void navigate('/t/menu', { replace: true });
    } catch (error) {
      setFailure(error);
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-6 pt-2">
      <Artwork name="cloche" className="mx-auto max-w-56" />
      <header className="-mt-2 flex flex-col items-center gap-1 text-center">
        <p className="text-xs font-semibold tracking-wide text-primary uppercase">Welcome to</p>
        <h1 className="text-3xl leading-9 font-bold text-ink">{context.restaurant_name}</h1>
        <p className="text-sm text-muted">{context.branch_name}</p>
      </header>

      <Panel className="flex items-center gap-3 px-4 py-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-primary-soft text-primary">
          <Icon name="pin" size={20} />
        </span>
        <p className="min-w-0">
          <span className="block text-xs font-medium text-muted">You are at</span>
          <span className="block text-xl leading-7 font-bold text-ink">
            {formatLocation(locationType, context.location_label)}
          </span>
          {context.zone_name && (
            <span className="block text-sm text-muted">{context.zone_name}</span>
          )}
        </p>
      </Panel>

      <form onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-5" noValidate>
        <Input
          label="Your name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          autoComplete="given-name"
          maxLength={100}
          required
          hint="Shown to staff so they can find you."
          error={attempted && nameMissing ? 'Enter your name to start.' : undefined}
        />
        {vocabulary.showsGuestCount && (
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-ink">Guests</span>
            <QuantityStepper value={guests} label="guests" onChange={setGuests} disabled={busy} />
          </div>
        )}
        {failureWords && (
          <Notice tone="error">
            <strong className="block">{failureWords.title}</strong>
            {failureWords.message}
          </Notice>
        )}
        <Button type="submit" variant="primary" size="lg" fullWidth loading={busy}>
          Start ordering
        </Button>
      </form>
    </div>
  );
}

/** The welcome screen's own shape while the venue loads: artwork, name, location card, two fields. */
function EntrySkeleton() {
  return (
    <LoadingRegion label="Loading your table">
      <div className="mx-auto flex w-full max-w-md flex-col items-center gap-6 pt-2">
        <Skeleton className="h-32 w-56 rounded-full" />
        <div className="flex w-full flex-col items-center gap-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-8 w-3/5" />
          <Skeleton className="h-4 w-1/3" />
        </div>
        <Skeleton className="h-20 w-full rounded-surface" />
        <div className="flex w-full flex-col gap-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-12 w-full" />
        </div>
        <Skeleton className="h-13 w-full" />
      </div>
      <SlowHint />
    </LoadingRegion>
  );
}
